import { getRectangleOverlap } from './rectangle';
import { Direction } from './types';
/**
 * Calculates the coords for all the available directions to a given
 * anchor and dimensions.
 *
 * ### Example (es module)
 * ```js
 * import getDirections from 'calculate-position'
 *
 * const anchor = popupAnchorElement.getBoundingClientRect()
 * const dimensions = {width: popupWidth, height: popupHeight}
 *
 * console.log(getDirections({anchor, dimensions}))
 * // => {Direction.BottomRight: {left: 100, top: 100}...}
 * ```
 *
 * @param anchor - anchor position that calculations will be based of
 * @param dimensions - width/height dimensions of popup element
 */
export const getDirections = ({ anchor, dimensions, }) => {
    return {
        [Direction.BottomRight]: {
            left: anchor.left + anchor.width,
            top: anchor.top + anchor.height,
        },
        [Direction.BottomLeft]: {
            left: anchor.left - dimensions.width,
            top: anchor.top + anchor.height,
        },
        [Direction.TopRight]: {
            left: anchor.left + anchor.width,
            top: anchor.top - dimensions.height,
        },
        [Direction.TopLeft]: {
            left: anchor.left - dimensions.width,
            top: anchor.top - dimensions.height,
        },
    };
};
/**
 * Returns the current window's dimensions. If it's unavilable returns 0
 *
 * ### Example (es module)
 * ```js
 * import getWindowDimensions from 'calculate-position'
 * *
 * console.log(getWindowDimensions()
 * // => {width: 800, height: 600}
 * ```
 */
export const getWindowDimensions = () => {
    if (typeof window == 'undefined') {
        return {
            width: 0,
            height: 0,
        };
    }
    return {
        width: window.innerWidth,
        height: window.innerHeight,
    };
};
/**
 * Calculates the best direction (e.g. BottomLeft) for a given
 * anchor and dimensions
 *
 * ### Example (es module)
 * ```js
 * import calculateBestDirection from 'calculate-position'
 *
 * const anchor = popupAnchorElement.getBoundingClientRect()
 * const dimensions = {width: popupWidth, height: popupHeight}
 *
 * console.log(calculateBestDirection({anchor, dimensions}))
 * // => Direction.TopLeft
 * ```
 *
 * @param anchor - anchor position that calculations will be based of
 * @param dimensions - width/height dimensions of popup element
 * @param viewport - (optional) viewport rectangle (defaults to window)
 */
export const calculateBestDirection = ({ anchor, dimensions, viewport = getWindowDimensions(), directions = getDirections({
    anchor,
    dimensions,
}), }) => {
    const viewportRectangle = {
        width: viewport.width,
        height: viewport.height,
        left: 0,
        top: 0,
    };
    let bestDirection = Direction.BottomLeft;
    let bestArea = 0;
    for (const direction in directions) {
        const directionRectangle = {
            ...directions[direction],
            ...dimensions,
        };
        const area = getRectangleOverlap(directionRectangle, viewportRectangle);
        if (area > bestArea) {
            bestArea = area;
            bestDirection = direction;
        }
    }
    return bestDirection;
};
//# sourceMappingURL=data:application/json;base64,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